// src/screens/TrainerDetails.tsx
import React, { useMemo, useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { bookSession, getTrainerProfileWithAvailability } from "../services/api";
import { useAuth } from "../contexts/AuthContext";
import { parseSlotToLocalTime, parseSlotISO } from "../utils/utcTimeUtils";

import Navbar from "../components/Navbar";
import TabBar from "../components/TabBar";
import DaySelector from "../components/DaySelector";
import { StatCard, TwoCol } from "../components/StatCard";
import MediaGallery, { MediaItem } from "../components/MediaGallery";
import BookingSuccessModal from "../components/BookingSuccessModal";

import trainerImg from "../assets/trainer.png";
import trainerPlaceholder from "../assets/image-trainer.png";
import TrainerImage from "../components/TrainerImage";
import { UPLOADS_BASE_URL } from "../config/api";

/* ------------------------------ small pieces ------------------------------ */

function SlotRow({
  time,
  available,
  onBook,
  booking,
}: {
  time: string;
  available: boolean;
  onBook: () => void;
  booking?: boolean;
}) {
  // Determine if this slot is disabled (not available but has a time)
  const isDisabled = !available && time;

  return (
    <div className={[
      "border border-[#E7E7E7] px-4 py-3 flex items-center justify-between relative",
      available ? "bg-white" : "bg-transparent"
    ].join(" ")}>
      {/* Disabled overlay for booked or past slots */}
      {isDisabled && (
        <div className="absolute inset-0 bg-gray-300 bg-opacity-5 z-10"></div>
      )}

      <div className="flex items-center gap-2">
        <span
          className={[
            "inline-block w-2 h-2 rotate-45 rounded-[1px]",
            available ? "bg-[#2FAE5E]" : "bg-[#D7B7B0]",
          ].join(" ")}
        />
        <span className={[
          "text-[14px]",
          available ? "text-black" : "text-gray-400"
        ].join(" ")}>{time}</span>
      </div>

      <button
        disabled={!available || booking}
        onClick={available ? onBook : undefined}
        className={[
          "h-[36px] px-4 flex items-center gap-2 font-bold text-[12px] tracking-wide",
          available && !booking
            ? "bg-[#EB2726] text-white hover:bg-[#D41E1C]"
            : "bg-[#F3C9C7] text-white/60 cursor-not-allowed",
        ].join(" ")}
      >
        {booking ? "BOOKING..." : "BOOK"}
        <svg width="20" height="16" viewBox="0 0 24 18" fill="none">
          <path d="M0 9H22.5" stroke="currentColor" strokeWidth="2" />
          <path d="M14.5 17L22.5 9L14.5 1" stroke="currentColor" strokeWidth="2" />
        </svg>
      </button>
    </div>
  );
}

/* ---------------------------------- screen --------------------------------- */

export default function TrainerDetails() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const location = useLocation();
  const { state } = location as {
    state?: {
      trainer?: {
        name: string;
        totalSessions: number;
        creditsPerHour: number;
        ageYears: number;
        gender: string;
        imageUrl?: string;
        about?: string;
        _id?: string; // Add trainer ID for booking
        fullTrainerData?: any; // Full trainer data including availability
      };
      from?: string; // Track where we came from
    };
  };

  const [bookingSlot, setBookingSlot] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [trainerAvailability, setTrainerAvailability] = useState<any>(null);
  const [loadingAvailability, setLoadingAvailability] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [bookingDetails, setBookingDetails] = useState<any>(null);

  // Debug logging
  console.log('TrainerDetails - Navigation state:', state);
  console.log('TrainerDetails - Current location:', window.location.pathname);
  console.log('TrainerDetails - Trainer data:', state?.trainer);
  console.log('TrainerDetails - Trainer ID:', state?.trainer?._id);
  console.log('TrainerDetails - Trainer name:', state?.trainer?.name);

  // Use real trainer data from API if available, otherwise fallback
  const trainer: {
    _id?: string;
    name: string;
    totalSessions: number;
    creditsPerHour: number;
    ageYears: number;
    gender: string;
    imageUrl?: string;
    about?: string;
  } = trainerAvailability ? {
    _id: trainerAvailability._id,
    name: trainerAvailability.name || "Trainer",
    totalSessions: trainerAvailability.totalSessions || 0,
    creditsPerHour: trainerAvailability.creditsPerHour || 0,
    ageYears: trainerAvailability.age ? parseInt(trainerAvailability.age) : 0,
    gender: trainerAvailability.sex || "Male",
    imageUrl: (() => {
      // Priority: 1. imageUrl from navigation state (from ExploreTrainers/Home), 2. signedProfilePic, 3. profilePic
      // This ensures we use the same image that was shown in the listing
      if (state?.trainer?.imageUrl) {
        const navImageUrl = state.trainer.imageUrl;
        // If it's already a full URL, use it as-is
        if (navImageUrl.startsWith('http://') || navImageUrl.startsWith('https://')) {
          return navImageUrl;
        }
        // If it's a key (starts with 'trainer/'), convert to full URL
        if (navImageUrl.startsWith('trainer/')) {
          return `${UPLOADS_BASE_URL}/${navImageUrl}`;
        }
        return navImageUrl;
      }
      // Fallback to signedProfilePic or profilePic from API
      const picUrl = trainerAvailability.signedProfilePic || trainerAvailability.profilePic;
      if (!picUrl) return trainerImg;
      // If it's already a full URL, use it as-is
      if (picUrl.startsWith('http://') || picUrl.startsWith('https://')) {
        return picUrl;
      }
      // If it's a key (starts with 'trainer/'), convert to full URL
      if (picUrl.startsWith('trainer/')) {
        return `${UPLOADS_BASE_URL}/${picUrl}`;
      }
      // Otherwise, assume it's already a URL or use fallback
      return picUrl || trainerImg;
    })(),
    about: trainerAvailability.aboutMe || "Professional trainer with years of experience",
  } : state?.trainer || {
    _id: "fallback-trainer-id",
    name: "HELENA PADILLA",
    totalSessions: 22,
    creditsPerHour: 2,
    ageYears: 28,
    gender: "Female",
    imageUrl: trainerImg,
    about:
      "A countdown timer picker in iOS style. This picker shows a countdown duration with hour, minute and second spinners. The duration is bound between 0 and 23.",
  };

  // Use availability data from navigation state or fetch if needed
  useEffect(() => {
    const fetchTrainerData = async () => {
      console.log('TrainerDetails - useEffect - trainer._id:', trainer._id);
      console.log('TrainerDetails - useEffect - trainer:', trainer);
      console.log('TrainerDetails - useEffect - fullTrainerData:', state?.trainer?.fullTrainerData);

      // If we have full trainer data from navigation state, use it
      if (state?.trainer?.fullTrainerData) {
        console.log('TrainerDetails - Using availability data from navigation state');
        setTrainerAvailability(state.trainer.fullTrainerData);
        setLoadingAvailability(false);
        return;
      }

      if (!trainer._id || trainer._id === "fallback-trainer-id") {
        console.log('TrainerDetails - useEffect - Skipping fetch due to invalid trainer ID');
        return;
      }

      try {
        setLoadingAvailability(true);

        // Fetch trainer profile with availability slots (calculated in backend)
        const availabilityData = await getTrainerProfileWithAvailability(trainer._id);

        setTrainerAvailability(availabilityData);

        console.log('Trainer availability data:', availabilityData);
        console.log('Trainer availability slots:', availabilityData?.availabilitySlots);
      } catch (error) {
        console.error('Error fetching trainer data:', error);
        setError('Failed to load trainer data');
      } finally {
        setLoadingAvailability(false);
      }
    };

    fetchTrainerData();
  }, [trainer._id, state?.trainer?.fullTrainerData]);

  // Function to handle booking a session
  const handleBookSession = (slotISO: string, displayTime: string) => {
    if (!user?.id || !trainer._id || trainer._id === "fallback-trainer-id") {
      setError('Unable to book session. Please try again.');
      return;
    }

    // Navigate to SelectAddress first, then to SessionDuration
    const selectedDate = days[selectedDay];
    const trainerWithSettings = {
      ...trainer,
      thresholdPeriod: trainerAvailability?.thresholdPeriod || 0,
      cooldownPeriod: trainerAvailability?.cooldownPeriod || 0,
    };

    navigate('/student/select-address', {
      state: {
        trainer: trainerWithSettings,
        slot: slotISO, // Pass the ISO slot string
        time: displayTime, // Pass display time for UI
        day: selectedDate,
      }
    });
  };

  const [activeTab, setActiveTab] = useState(0); // 0: Availability, 1: About
  const [selectedDay, setSelectedDay] = useState(0);

  // Generate next 7 days starting from today
  const days = useMemo(() => {
    const today = new Date();
    const next7Days = [];

    console.log('Generating calendar days, today is:', today.toISOString());
    console.log('Local date:', today.toLocaleDateString());
    console.log('UTC date:', today.toISOString().split('T')[0]);

    for (let i = 0; i < 7; i++) {
      // Create a new date for each day using local time
      const date = new Date();
      date.setDate(today.getDate() + i); // Use setDate instead of setUTCDate

      const dayNames = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
      const fullDayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
      const monthNames = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];

      // Format date in YYYY-MM-DD format using local date (not UTC)
      const year = date.getFullYear();
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const day = date.getDate().toString().padStart(2, '0');
      const fullDate = `${year}-${month}-${day}`;

      const dayData = {
        date: `${monthNames[date.getMonth()]} ${date.getDate()}`, // Use getMonth() and getDate()
        day: dayNames[date.getDay()], // Use getDay()
        fullDate: fullDate, // YYYY-MM-DD format using local date
        dayOfWeek: fullDayNames[date.getDay()], // Use full day names for API matching
        isToday: i === 0, // Mark if this is today
        dateObj: new Date(date) // Store the date object for time comparisons
      };

      console.log(`Day ${i}:`, dayData);
      next7Days.push(dayData);
    }

    return next7Days;
  }, []);

  // Generate slots from backend availabilitySlots (no calculations needed)
  const slotsByDay = useMemo(() => {
    console.log('TrainerDetails - Using backend slots, trainerAvailability:', trainerAvailability);
    console.log('TrainerDetails - availabilitySlots:', trainerAvailability?.availabilitySlots);
    console.log('TrainerDetails - Days data:', days);

    if (!trainerAvailability?.availabilitySlots) {
      console.log('TrainerDetails - No availability slots from backend, returning empty slots');
      return {};
    }

    const slots: Record<number, { time: string; available: boolean; slotISO: string }[]> = {};

    days.forEach((day, index) => {
      // Get slots for this day from backend (already calculated)
      const daySlots = trainerAvailability.availabilitySlots[day.fullDate] || [];

      // Remove duplicates by slotISO before processing
      const uniqueSlots = daySlots.reduce((acc: any[], slot: any) => {
        const exists = acc.find(s => s.slot === slot.slot);
        if (!exists) {
          acc.push(slot);
        }
        return acc;
      }, []);

      // Convert UTC ISO slots to local time for display
      slots[index] = uniqueSlots.map((slot: any) => ({
        time: parseSlotToLocalTime(slot.slot), // Convert UTC to local time for display
        available: slot.available, // Use backend calculated availability
        slotISO: slot.slot // Keep ISO string for booking
      }));

      console.log(`TrainerDetails - Slots for ${day.fullDate}:`, {
        original: daySlots.length,
        unique: uniqueSlots.length,
        slots: slots[index]
      });
    });

    console.log('TrainerDetails - Final slots by day:', slots);
    return slots;
  }, [trainerAvailability, days]);

  const todaysSlots = slotsByDay[selectedDay] ?? [];

  // Modal handlers
  const handleCloseSuccessModal = () => {
    setShowSuccessModal(false);
    setBookingDetails(null);
    navigate('/student/home');
  };

  const handleMessageTrainer = () => {
    // TODO: Implement messaging functionality
    console.log('Message trainer:', trainer.name);
    setShowSuccessModal(false);
    navigate('/student/home');
  };

  // Media for ABOUT (videos open in a new tab via MediaGallery)
  const { videos, photos } = useMemo(() => {
    const mediaItems: MediaItem[] = [];

    // Add training videos from API
    if (trainerAvailability?.trainingVideoLinks && Array.isArray(trainerAvailability.trainingVideoLinks)) {
      trainerAvailability.trainingVideoLinks.forEach((videoUrl: string, index: number) => {
        mediaItems.push({
          type: "video",
          url: videoUrl,
          title: `Training Video ${index + 1}`
        });
      });
    }

    // Add training photos from API
    if (trainerAvailability?.trainingPhotos && Array.isArray(trainerAvailability.trainingPhotos)) {
      trainerAvailability.trainingPhotos.forEach((photoUrl: string, index: number) => {
        mediaItems.push({
          type: "image",
          src: photoUrl,
          title: `Training Photo ${index + 1}`
        });
      });
    }

    // If no media from API, show placeholder
    if (mediaItems.length === 0) {
      mediaItems.push(
        { type: "image", src: trainerPlaceholder, title: "No training photos available" }
      );
    }

    return {
      videos: mediaItems.filter(m => m.type === "video"),
      photos: mediaItems.filter(m => m.type === "image")
    };
  }, [trainerAvailability]);

  return (
    <div
      className="h-[100dvh] w-full bg-[#F7F7F7] overflow-y-auto"
      style={{ WebkitOverflowScrolling: "touch" }}
    >
      <div className="mx-auto w-full min-h-full flex flex-col">
        {/* TRAINER HEADER */}
        <div className="relative bg-gradient-to-br from-[#3a3a3a] to-[#252525] text-white">
          {/* NAVBAR INSIDE HEADER */}
          <Navbar
            onBack={() => {
              // Navigate back to the page we came from, or default to home
              const fromRoute = state?.from || '/student/home';
              navigate(fromRoute);
            }}
            background="transparent"
            spacerHeight={40}
          />

          <div className="relative px-5 pb-7">
            <div className="relative pr-28">
              <h1 className="text-[#eb2726] font-extrabold text-2xl leading-tight tracking-tight italic mb-2">
                {trainer.name.split(" ")[0]}
              </h1>
              {trainer.name.split(" ").slice(1).join(" ") && (
                <h1 className="text-[#eb2726] font-extrabold text-2xl leading-tight tracking-tight italic mb-2">
                  {trainer.name.split(" ").slice(1).join(" ")}
                </h1>
              )}

              <p className="text-[#e6e6e6] font-semibold text-sm mb-2">
                <span className="font-extrabold">{trainer.totalSessions}</span> total sessions
              </p>

              <div className="flex items-center gap-2 font-semibold text-sm">
                <svg className="w-4 h-4 text-[#FFC800]" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
                </svg>
                <span className="text-white">{trainer.creditsPerHour}</span>
                <span className="text-[#B0B0B0]">credits per hour</span>
              </div>
            </div>

            <TrainerImage
              src={trainer.imageUrl || trainerImg}
              alt={trainer.name}
              className="absolute right-0 bottom-0 w-28 h-auto object-contain pointer-events-none select-none"
            />
          </div>
        </div>

        {/* CONTENT */}
        <main className="flex-1 bg-white">
          <TabBar
            tabs={["AVAILABILITY", "ABOUT"]}
            active={activeTab}
            onChange={setActiveTab}
          />

          {activeTab === 0 ? (
            <section className="px-5 pb-20">
              <div className="mt-4">
                <DaySelector
                  days={days}
                  selectedDay={selectedDay}
                  setSelectedDay={setSelectedDay}
                />
              </div>

              {/* Error message */}
              {error && (
                <div className="mt-4 p-3 bg-red-100 border border-red-300 rounded text-red-700 text-sm">
                  {error}
                </div>
              )}

              {loadingAvailability ? (
                <div className="mt-4 p-6 text-center text-gray-500">
                  Loading availability...
                </div>
              ) : todaysSlots.length > 0 ? (
                <div className="mt-4 grid gap-4">
                  {todaysSlots.map((s, idx) => (
                    <SlotRow
                      key={s.slotISO || idx} // Use slotISO as key to prevent duplicates
                      time={s.time}
                      available={s.available}
                      booking={bookingSlot === s.slotISO}
                      onBook={() => {
                        if (!s.available) return;
                        handleBookSession(s.slotISO, s.time);
                      }}
                    />
                  ))}
                </div>
              ) : (
                <div className="mt-4 p-6 text-center text-gray-500">
                  No available slots for {days[selectedDay]?.day}
                </div>
              )}
            </section>
          ) : (
            <section className="px-5 pb-20">
              {/* about blurb */}
              <div className="mt-5 bg-white border border-[#DEDEDE] rounded">
                <div className="p-4">
                  <p className="text-sm leading-relaxed text-[#2d2d2d]">
                    {trainer.about}
                  </p>
                </div>
              </div>

              {/* stats */}
              <div className="mt-5">
                <TwoCol>
                  <StatCard label="Age" value={`${trainer.ageYears} years`} />
                  <StatCard label="Gender" value={trainer.gender} />
                </TwoCol>
              </div>

              {/* media stack */}
              <div className="mt-5 space-y-4">
                {videos.length > 0 && <MediaGallery items={videos} />}
                {photos.length > 0 && <MediaGallery items={photos} />}
              </div>
            </section>
          )}
        </main>
      </div>

      {/* Booking Success Modal */}
      <BookingSuccessModal
        isOpen={showSuccessModal}
        onClose={handleCloseSuccessModal}
        bookingDetails={bookingDetails}
        onMessageTrainer={handleMessageTrainer}
      />
    </div>
  );
}
